import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { HelpCircle, TrendingUp, Brain, Heart, Trophy, Award, Star } from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { ScoreBreakdown } from '@/types/survey';
import { useLanguage } from '@/contexts/LanguageContext';

interface ScoreCardsProps {
  scoreBreakdown: ScoreBreakdown;
}

const ScoreCards: React.FC<ScoreCardsProps> = ({ scoreBreakdown }) => {
  const { t } = useLanguage();
  
  const nevExplanation = t('metrics.nev_description') || "NEV (Net Emotional Value) measures the overall emotional impact and brand resonance of your project. It reflects the strength of user emotional connection, brand loyalty, and community engagement. A higher NEV indicates a stronger positive emotional impact.";
  const eroiExplanation = t('metrics.eroi_description') || "EROI (Emotional Return on Investment) evaluates the transformative impact, long-term industry relevance, and legacy value of your project. It assesses how effectively your project converts emotional engagement into tangible actions like investment or advocacy.";
  const beamScoreExplanation = t('results.understandingBeamScore') || "The BEAM Score measures emotional and strategic value across diverse asset classes on a scale of 0-100. Higher scores indicate exceptional emotional resonance, strong ethical alignment, and high adaptability potential.";
  const emotionResonanceExplanation = "BEAM Score is a core weighted Emotion Resonance metric that quantifies the emotional connection, brand loyalty, and engagement potential of your project on a scale of 0-100.";

  const safeToFixed = (value: number | undefined, decimals = 2) => {
    if (value === undefined || value === null) return '0.00';
    return Number(value).toFixed(decimals);
  };
  
  // Convert BEAM score to 0-100 scale
  const beamScoreScaled = scoreBreakdown?.overallScore ? Math.min(100, scoreBreakdown.overallScore * 10) : 0;
  const formattedBeamScore = safeToFixed(beamScoreScaled, 1);
  
  // Determine certification level based on score
  const getCertificationBadge = (score: number) => {
    if (score >= 90) {
      return (
        <div className="flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
          <Trophy className="h-3.5 w-3.5 text-yellow-600" />
          BEAM Gold
        </div>
      );
    } else if (score >= 80) {
      return (
        <div className="flex items-center gap-1 bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">
          <Award className="h-3.5 w-3.5 text-gray-500" />
          BEAM Silver
        </div>
      );
    } else if (score >= 70) {
      return (
        <div className="flex items-center gap-1 bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-medium">
          <Star className="h-3.5 w-3.5 text-amber-600" />
          BEAM Bronze
        </div>
      );
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    <Brain className="h-4 w-4 mr-1.5 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">BEAM Score</span>
                    <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{beamScoreExplanation}</p>
                  <p className="text-xs mt-1">{emotionResonanceExplanation}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="flex flex-col items-end">
              <span className="text-2xl font-bold text-blue-600">{formattedBeamScore}</span>
              <span className="text-xs text-blue-500">/ 100</span>
            </div>
          </div>
          <div className="w-full bg-white/50 rounded-full h-2 mt-2">
            <div 
              className="bg-blue-500 h-2 rounded-full" 
              style={{ width: `${Math.min(100, beamScoreScaled)}%` }}
            ></div>
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="text-xs text-blue-800/70">
              BaaBaa Group™ Proprietary Metric
            </div>
            {getCertificationBadge(beamScoreScaled)}
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    <Heart className="h-4 w-4 mr-1.5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">{t('metrics.nev')}</span>
                    <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{nevExplanation}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-2xl font-bold text-purple-600">{safeToFixed(scoreBreakdown.nev)}</span>
          </div>
          <div className="w-full bg-white/50 rounded-full h-2 mt-2">
            <div 
              className="bg-purple-500 h-2 rounded-full" 
              style={{ width: `${Math.min(100, (scoreBreakdown.nev / 50) * 100)}%` }}
            ></div>
          </div>
          <div className="text-xs text-purple-800/70 mt-2 text-right">
            AI-Calculated Emotional Value
          </div>
        </CardContent>
      </Card>
      
      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 shadow-sm">
        <CardContent className="pt-4">
          <div className="flex justify-between items-center mb-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center cursor-help">
                    <TrendingUp className="h-4 w-4 mr-1.5 text-green-600" />
                    <span className="text-sm font-medium text-gray-700">{t('metrics.eroi')}</span>
                    <HelpCircle className="h-3.5 w-3.5 ml-1 text-gray-400" />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">{eroiExplanation}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-2xl font-bold text-green-600">{safeToFixed(scoreBreakdown.eroi)}x</span>
          </div>
          <div className="w-full bg-white/50 rounded-full h-2 mt-2">
            <div 
              className="bg-green-500 h-2 rounded-full" 
              style={{ width: `${Math.min(100, (scoreBreakdown.eroi / 5) * 100)}%` }}
            ></div>
          </div>
          <div className="text-xs text-green-800/70 mt-2 text-right">
            Emotional ROI Metric
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScoreCards;
